import React, { useMemo } from 'react';
import { useTimeSheetQuery } from '../../__generated__/graphql';
import Loading from './Loading';
import ServerError from '../../../../components/ServerError';
import { Row, Col } from 'react-bootstrap';
import GoalChartCard from './GoalChartCard';
import WeekChartCard from './WeekChartCard';
import TimeDistChartCard from './TimeDistChartCard';
import { EmployeeInfo } from './EmployeeInfo';
import TimeSheetActionsPanel from './TimeSheetActionsPanel';
import TimeTrackingEntries from '../TimeTracking/TimeTrackingEntries';
import { TimeSheet } from './types';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import { useFlairBreadcrumbHook } from '../../../../hooks/useFlairBreadcrumbHook';
import { routes } from '../../routes';
import {
  TimeSheetApproveContextProvider,
  TimeSheetApproveModalConnected,
} from '../../components/TimeSheetApprove';
import { TimeTrackingProvider } from '../../hooks/timeTrackingSettings/TimeTrackingContext';
import { mapProject } from '../Projects/mapping';
import { ProjectWithStatus } from '../Projects/types';
import { getProjectStatus } from '../Projects/ProjectsTimesheets/helpers';
import moment from 'moment';

type Props = {
  timeSheetId: string;
  goToAllTimesheetsAction?: (timeSheet?: TimeSheet) => void;
};

const TimeSheetContent: React.FC<Props> = ({
  timeSheetId,
  goToAllTimesheetsAction,
}) => {
  const { data, loading, error } = useTimeSheetQuery({
    variables: { id: timeSheetId },
  });

  const t = useNamespacedTranslation('navigation.menuItems.timeManagement');

  useFlairBreadcrumbHook(
    [
      { label: t('title') },
      {
        label: t('subMenuItems.controlling.title'),
        url: routes.timeSheetsControllingPending.route.create({}),
      },
      { label: data?.timeSheet.employee.Name ?? '' },
    ],
    loading,
  );

  const timeSheet = data?.timeSheet;

  const projects: ProjectWithStatus[] = useMemo(() => {
    const projectsData = data?.projects;
    if (!projectsData || !timeSheet) return [];
    return projectsData.map((project) => {
      const mapped = mapProject(project);
      return {
        ...mapped,
        status: getProjectStatus(
          mapped,
          moment(timeSheet.flair__Start_Date__c),
        ),
      };
    });
  }, [data?.projects, timeSheet]);

  if (loading) return <Loading />;

  if (error) return <ServerError />;

  if (!timeSheet) {
    return <></>;
  }

  return (
    <TimeSheetApproveContextProvider>
      <TimeTrackingProvider employeeId={timeSheet.flair__Employee__c}>
        <Row>
          <Col lg={12}>
            <TimeSheetActionsPanel
              goToAllTimesheetsAction={goToAllTimesheetsAction}
              timeSheet={timeSheet}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={4}>
            <GoalChartCard timeSheet={timeSheet} />
          </Col>
          <Col lg={4}>
            <WeekChartCard timeSheet={timeSheet} />
          </Col>
          <Col lg={4}>
            <TimeDistChartCard timeSheet={timeSheet} />
          </Col>
        </Row>

        <Row>
          <Col xl={8}>
            <TimeTrackingEntries
              timeSheet={timeSheet}
              costCenters={data.costCenters}
              projects={projects}
            />
          </Col>
          <Col xl={4}>
            <EmployeeInfo employee={timeSheet.employee} />
          </Col>
        </Row>
        <TimeSheetApproveModalConnected />
      </TimeTrackingProvider>
    </TimeSheetApproveContextProvider>
  );
};

export default TimeSheetContent;
